import { useBoolean, useId } from '@fluentui/react-hooks';
import * as React from 'react';
import { emptyAttendee, IAttendee } from '../../../../Models/Attendees/IAttendee';
import { IStatusMessage } from '../../../../Models/IStatusMessage';
import { AttendeesListView } from '../../../Attendees/AttendeesList/AttendeesListView';
import * as Services from '../../../../Services/Services';
import { IAdminApi } from '../../../../Services/API/Admin/IAdminApi';
import { SelectionMode,DefaultButton, Dialog, DialogFooter, DialogType, MessageBar, MessageBarType, PrimaryButton, Stack, Label } from '@fluentui/react';
import { AttendeePropertiesAdmin } from '../AttendeeProperties/AttendeePropertiesAdmin'

interface IAttendeesListProps {
    userId: string;
    street: string;
    city: string;
    postalCode: string;
    phoneNumber: string;
    email: string;

    selectionChanged?: (selection: IAttendee[]) => void;
    selectionMode: SelectionMode;
    allowDelete: boolean;
}

const dialogStyles = { main: { maxWidth: 450 } };
export const AttendeesList: React.FunctionComponent<IAttendeesListProps> = (props) => {
    const [attendeeList, setAttendeeList] = React.useState<IAttendee[]>([]);
    const [isLoading, setIsLoading] = useBoolean(false);
    const [statusMessage, setStatusMessage] = React.useState<IStatusMessage>()
    const [isPropertiesOpened, setIsPropertiesOpened] = useBoolean(false);
    const [selectedAttendee, setSelectedAttendee] = React.useState<IAttendee>()

    const [isDeleteDialogOpened, setIsDeleteDialogOpened] = useBoolean(false);
    const [eventTypeToDelete, setEventTypeToDelete] = React.useState({ id: 0, desc: "" });

    const labelId: string = useId('dialogLabel');
    const subTextId: string = useId('subTextLabel');

    const modalProps = React.useMemo(
        () => ({
            titleAriaId: labelId,
            subtitleAriaId: subTextId,
            isBlocking: false,
            styles: dialogStyles,
            dragOptions: undefined,
        }),
        [labelId, subTextId],
    );

    const loadData = async () => {
        setIsLoading.setTrue();
        setStatusMessage(undefined);
        const adminUserApi = Services.ServicesProvider.GetService<IAdminApi>(Services.AdminApi);
        const eventType = await adminUserApi.getAttendees(props.userId);

        if (eventType.isSuccess) {
            if (eventType.data && eventType.data.length > 0) {
                setStatusMessage({ message: eventType.message, type: MessageBarType.success });
                setAttendeeList(eventType.data);
            }
            else {
                setStatusMessage({ message: "Nebyli nalezeni žádní žáci.", type: MessageBarType.warning });
            }
        }
        else {
            setStatusMessage({ message: eventType.message, type: MessageBarType.error });
        }
        setIsLoading.setFalse();
    }

    React.useEffect(() => {
        if (props.userId && props.userId != "") { 
            loadData();
        }
    }, [])

    React.useEffect(() => {
        if (props.userId && props.userId != "") {
            loadData();
        }
    }, [props.userId])
   
    const deleteAttendee = async (id: number,userId:string) => {
        setIsLoading.setTrue();
        setStatusMessage(undefined);
        const eventTypeApi = Services.ServicesProvider.GetService<IAdminApi>(Services.AdminApi);
        const result = await eventTypeApi.deleteAttendee(id, userId);

        if (result.isSuccess) {
            setStatusMessage({ message: result.message, type: MessageBarType.success });
            await loadData();
        }
        else {
            setStatusMessage({ message: result.message, type: MessageBarType.error });
        }
        setIsLoading.setFalse();
        setIsDeleteDialogOpened.setFalse();
    }

    const selectAttendee = (items: IAttendee[]) => {
        if (props.selectionMode === SelectionMode.single && items.length===1) {
            setSelectedAttendee(items[0]);
            setIsPropertiesOpened.setTrue();
        }
        props.selectionChanged && props.selectionChanged(items);
    }

    const addAttendee = () => {
        setSelectedAttendee(emptyAttendee(props.userId, props.street, props.city, props.postalCode, props.phoneNumber, props.email));
        setIsPropertiesOpened.setTrue();
    }

    const refresh = async (id: number) => {
        await loadData();
    }

    const deleteItemDialog = (id: number, desc: string) => {
        setEventTypeToDelete({ id: id, desc: desc });
        setIsDeleteDialogOpened.setTrue();
    }

    const deleteClick = async () => {
        if (selectedAttendee) {
            await deleteAttendee(selectedAttendee.id, selectedAttendee?.userId);
        }
    }

    return (<>
        <AttendeesListView userId={props.userId} selectionMode={props.selectionMode} deleteItem={props.allowDelete ? deleteItemDialog : undefined} selectItem={selectAttendee} isLoading={isLoading} items={attendeeList} statusMessage={statusMessage} addAttendee={addAttendee} />
        {statusMessage && statusMessage.message ? <MessageBar messageBarType={statusMessage.type}>{statusMessage.message}</MessageBar> : null}
        {selectedAttendee ?
            <AttendeePropertiesAdmin item={selectedAttendee}
                isOpen={isPropertiesOpened}
                close={setIsPropertiesOpened.setFalse}
                refresh={refresh}
                /> 
           : null}
            
        {isDeleteDialogOpened ?
            <Dialog
                hidden={!isDeleteDialogOpened}
                onDismiss={setIsDeleteDialogOpened.setFalse}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Odstranit objekt',
                    closeButtonAriaLabel: 'Zavřít',
                    subText: `Opravdu chcete odstranit událost ${eventTypeToDelete.desc} ?`,
                }}
                modalProps={modalProps}
            >
                <DialogFooter>
                    <PrimaryButton onClick={deleteClick} text="Odstranit" />
                    <DefaultButton onClick={setIsDeleteDialogOpened.setFalse} text="Zrušit" />
                </DialogFooter>
            </Dialog> : null}
    </>
    )
}

AttendeesList.defaultProps = { selectionMode: SelectionMode.single }
